import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import messages from '../../lang/en';
import { theme } from '../../theme';
import { RestoreIcon, RetryIcon, TrashIcon, UploadCloudIcon } from '../UI/Icons';
import Compressor from 'compressorjs';
import { IFormPresentation } from '../../schema/interface';
import InputContext from '../../context/InputContext';
import { SET_FIELD } from '../../hooks/reducer';
import CircleLoader from '../UI/CircleLoader';
import { IMAGE_FILE_TYPES } from '../../constants';
import FormStructureContext from '../../context/FormStructureContext';

const styles = createUseStyles({
    container: {
        position: 'relative',
        display: 'flex',
        '& img': {
            maxWidth: '100%',
            maxHeight: '230px',
            cursor: 'auto',
            margin: 'auto'
        },
        '&:hover *': {
            opacity: '1'
        }
    },
    mediaPlaceholder: {
        width: '100%',
        height: '83px',
        border: '1.7px solid',
        borderColor: theme.grayColor5,
        color: theme.grayColor3,
        borderRadius: '7px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexFlow: 'column',
        padding: '8px',
        cursor: 'pointer',
        position: 'relative',
        '& > div': {
            marginTop: '4px'
        }
    },
    inputFile: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        cursor: 'pointer',
        opacity: '0 !important',
        '&::-webkit-file-upload-button': {
            webkitAppearance: 'button',
            cursor: 'pointer'
        }
    },
    centerContainer: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: '10',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end ',
        color: 'white',
        '& > div': {
            cursor: 'pointer'
        }
    },
    innerContainer: {
        display: 'flex',
        flexFlow: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '0 7px',
        position: 'relative',
        fontSize: '12px',
        '& > *': {
            display: 'flex',
            background: 'rgba(0 0 0 / 50%)',
            borderRadius: '4px',
            boxSizing: 'content-box',
            padding: '7px',
            cursor: 'pointer',
            textAlign: 'center'
        },
        '&  svg': {
            margin: '7px',
            width: '20px',
            height: '20px'
        }
    },
    actionContainer: {
        opacity: '0',
        zIndex: '10',
        padding: '2.5px',
        fontSize: '13px',
        display: 'flex',
        '& *': {
            opacity: '0'
        }
    },
    deletedImg: {
        filter: 'grayscale(0.5) blur(1px)'
    },
    notUploaded: {
        filter: 'blur(1px)'
    },
    alert: {
        fontSize: '13px',
        color: 'red'
    },
    row: {
        display: 'flex',
        alignItems: 'flex-start'
    }
});
interface MediaProps {
    value?: string;
    name: string;
    uploadMedia: Function;
    label: string;
    presentation: IFormPresentation;
    noAccess?: boolean;
}
interface IInputFile {
    title: string;
    width?: string;
    height?: string;
    required?: boolean;
}
const MediaInput = ({ value, name, uploadMedia, label, presentation, noAccess }: MediaProps) => {
    const classes = styles();
    const [image, setImage] = useState<{
        url: string;
        name?: string;
        type?: string;
    }>(null);
    const [error, setError] = useState(null);
    const [errorDisplay, setErrorDisplay] = useState(false);
    const [isDeleted, setIsDeleted] = useState(false);
    const { dispatch } = useContext(InputContext);
    const { media } = useContext(FormStructureContext);

    useEffect(() => {
        if (media?.uploadedLink) {
            dispatch({
                type: SET_FIELD,
                name: media.name,
                value: media.uploadedLink
            });
            setImage(null);
        }
    }, [media?.uploadedLink]);

    useEffect(() => {
        if (media === 'isSubmitted') {
            setImage(null);
            setIsDeleted(false);
        }
    }, [media]);

    const handleSelectMedia = (e: ChangeEvent<HTMLInputElement>) => {
        setError(null);
        setImage(null);
        const input = e.target;
        const selectedFile = input.files ? input.files[0] : null;
        if (!IMAGE_FILE_TYPES.includes(selectedFile.type)) {
            setError('Please upload png or jpeg image.');
            return;
        }
        new Compressor(selectedFile, {
            quality: 0.6,
            convertSize: 10000000,
            /* eslint-disable no-restricted-syntax */
            success(result) {
                const file = new File([result], selectedFile.name, {
                    type: selectedFile.type,
                    lastModified: selectedFile.lastModified
                });

                uploadMedia({ file, fieldName: name });
                const url = URL.createObjectURL(result);
                setImage({
                    url,
                    name: selectedFile.name,
                    type: selectedFile.type
                });
            }
        });
    };
    const removeImageFieldValue = () => {
        dispatch({
            type: SET_FIELD,
            name,
            value: null
        });
    };
    const handleRemoveUploadedFile = () => {
        setImage({ url: value });
        setIsDeleted(true);
        removeImageFieldValue();
    };
    const handleRestoreImg = () => {
        dispatch({
            type: SET_FIELD,
            name,
            value: image.url
        });
        setImage(null);
        setIsDeleted(false);
    };
    const handleRetryUploading = () => {
        const file = new File([image.url], image.name, { type: image.type });
        uploadMedia({ file, fieldName: name });
    };
    const handleError = () => {
        setErrorDisplay(true);
    };
    const handleDisplayMedia = () => {
        setErrorDisplay(false);
    };
    const InputFile = ({ title, width, height, required }: IInputFile) => {
        return (
            <>
                <input
                    className={classes.inputFile}
                    type="file"
                    name="field-media"
                    accept="image/*"
                    onChange={handleSelectMedia}
                    required={required}
                    disabled={noAccess}
                />
                <UploadCloudIcon width={width} height={height} />
                <div>{title}</div>
            </>
        );
    };
    if (errorDisplay) {
        return (
            <div>
                <div className={classes.mediaPlaceholder} onClick={handleDisplayMedia}>
                    <RetryIcon fill="#aaadb3" />
                    <div>{messages?.errorDisplayImg}</div>
                </div>
            </div>
        );
    }
    if (image?.url) {
        return (
            <div className={classes.container}>
                <img
                    className={isDeleted ? classes.deletedImg : classes.notUploaded}
                    src={image?.url}
                    alt={label}
                />
                <div className={classes.centerContainer}>
                    {isDeleted ? (
                        <div className={classes.innerContainer} onClick={handleRestoreImg}>
                            <RestoreIcon fill="#fff" width="23px" height="23px" />
                            <div>Restore</div>
                        </div>
                    ) : media?.status === 'error' ? (
                        <div className={classes.row}>
                            <div
                                className={classes.innerContainer}
                                onClick={() => {
                                    setImage(null);
                                    removeImageFieldValue();
                                }}
                            >
                                <TrashIcon width="20px" height="20px" />
                                <div>Remove</div>
                            </div>
                            <div className={classes.innerContainer} onClick={handleRetryUploading}>
                                <RetryIcon fill="#fff" />
                                <div>Retry uploading</div>
                            </div>
                        </div>
                    ) : (
                        <CircleLoader
                            width="23px"
                            height="23px"
                            borderWidth="2px"
                            borderTopColor="#000"
                        />
                    )}
                </div>
            </div>
        );
    }
    if (value) {
        return (
            <div className={classes.container}>
                <img src={value} alt={label} onError={handleError} />
                <div className={classes.centerContainer}>
                    <div className={classes.actionContainer}>
                        <div className={classes.innerContainer} onClick={handleRemoveUploadedFile}>
                            <TrashIcon width="20px" height="20px" />
                            <div>Remove</div>
                        </div>
                        <div className={classes.innerContainer}>
                            <InputFile title="Replace" width="23px" height="23px" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div>
            <div className={classes.mediaPlaceholder}>
                <InputFile title={messages?.uploadImg} required={presentation?.required} />
            </div>
            {error && <div className={classes.alert}>{error}</div>}
        </div>
    );
};
export default MediaInput;
